import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { consClusterToLabel, ConstructionCluster } from '../../model/geo/ConstructionCluster';
import { BY_ALPHA_2 } from '../../model/geo/Countries';
import { CountryAlpha2Code } from '../../model/geo/CountryAlpha2Code';
import { devClusterToLabel, DevelopmentCluster } from '../../model/geo/DevelopmentCluster';
import { GlobalRegion, globalRegionToLabel } from '../../model/geo/GlobalRegion';
import { ProjectFilter } from '../../model/ProjectFilter';
import { phaseToLabel, ProjectPhase } from '../../model/general/ProjectPhase';
import { ProjectStage, stageToLabel } from '../../model/general/ProjectStage';
import { ProjectStatus, statusToLabel } from '../../model/general/ProjectStatus';
import { ProjectType, projectTypeToLabel } from '../../model/general/ProjectType';
import { Technology, technologyToLabel } from '../../model/general/Technology';
import { ProjectFilterService } from '../../services/project-list/project-filter.service';
import { ProjectListService } from '../../services/project-list/project-list.service';
import { BusinessModel, businessModelToLabel } from '../../model/general/BusinessModel';
import { RouteToMarket, routeToMarketToLabel } from '../../model/general/RouteToMarket';
import { devDirectorateToLabel, DevelopmentDirectorate } from '../../model/geo/DevelopmentDirectorate';
import { ProjectFilterTechnologyComponent } from './project-filter-technology/project-filter-technology.component';
import { HybridProjectFilterOption, hybridProjectFilterOptionToLabel } from '../../model/general/HybridProjectFilterOption';
import { Subject } from 'rxjs';
import { GetProjectManagers } from '../../services/user/GetProjectManagers';
import { MyProjectsFilterOption, myProjectsFilterOptionToLabel } from '../../model/general/MyProjectsFilterOption';
import {
  ProjectManagingApplication,
  ProjectManagingApplicationMode,
  projectManagingApplicationModeToLabel,
  projectManagingApplicationToLabel
} from '../../model/general/ProjectManagingApplication';
import { ProjectValidationFilter, projectValidationFilterToLabel } from '../../model/validation/ProjectValidationFilter';
import { ValidationLockFilter, validationLockFilterToLabel } from '../../model/validation/ValidationLockFilter';

@Component({
  selector: 'digires-project-filter-sidebar',
  templateUrl: './project-filter-sidebar.component.html',
  styleUrls: ['./project-filter-sidebar.component.scss']
})
export class ProjectFilterSidebarComponent implements OnInit, OnDestroy {
  destroy$: Subject<boolean> = new Subject<boolean>();

  readonly allManagingApps: ProjectManagingApplication[] = Object.values(ProjectManagingApplication);
  readonly allManagingAppModes: ProjectManagingApplicationMode[] = Object.values(ProjectManagingApplicationMode);
  readonly allTechnologies: Technology[] = Object.values(Technology);
  readonly allTypes: ProjectType[] = Object.values(ProjectType);
  readonly allStatuses: ProjectStatus[] = Object.values(ProjectStatus);
  readonly allPhases: ProjectPhase[] = Object.values(ProjectPhase);
  readonly allStages: ProjectStage[] = Object.values(ProjectStage);
  readonly allGlobalRegions: GlobalRegion[] = Object.values(GlobalRegion);
  readonly allDevelopmentClusters: DevelopmentCluster[] = Object.values(DevelopmentCluster);
  readonly allConstructionClusters: ConstructionCluster[] = Object.values(ConstructionCluster);
  readonly allBusinessModels: BusinessModel[] = Object.values(BusinessModel);
  readonly allRouteToMarket: RouteToMarket[] = Object.values(RouteToMarket);
  readonly allDevelopmentDirectorates: DevelopmentDirectorate[] = Object.values(DevelopmentDirectorate);
  readonly allHybridProjectFilterOptions: HybridProjectFilterOption[] = Object.values(HybridProjectFilterOption);
  readonly allMyProjectsFilterOptions: MyProjectsFilterOption[] = Object.values(MyProjectsFilterOption);
  readonly allProjectValidations: ProjectValidationFilter[] = Object.values(ProjectValidationFilter);
  readonly allValidationLocks: ValidationLockFilter[] = Object.values(ValidationLockFilter);

  allCountryCodes: CountryAlpha2Code[] = null;
  allRegions: { label: string, items: any[] }[] = [];
  allManagers: FilterableManager[] = [];
  userIdToManager: { [userId: string]: FilterableManager } = {};

  @ViewChild(ProjectFilterTechnologyComponent) techFilter: ProjectFilterTechnologyComponent;
  @Output() onClose = new EventEmitter<void>();
  formGroup: FormGroup;
  updating: boolean;

  constructor(
    private projectListService: ProjectListService,
    private filterService: ProjectFilterService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      name: new FormControl(),
      projectManager: new FormControl(),
      myProjects: new FormControl(),
      hybridProject: new FormControl(),
      managingApplicationMode: new FormControl(),
      managingApplication: new FormControl(),
      status: new FormControl(),
      type: new FormControl(),
      phase: new FormControl(),
      stage: new FormControl(),
      businessModel: new FormControl(),
      routeToMarket: new FormControl(),
      developmentDirectorate: new FormControl(),
      country: new FormControl(),
      globalRegion: new FormControl(),
      devCluster: new FormControl(),
      consCluster: new FormControl(),
      region: new FormControl(),
      teams: new FormControl(),
      projectValidation: new FormControl(),
      validationLock: new FormControl(),
    });
    let oldValue = this.formGroup.value;
    let processingChanges = false;
    this.formGroup.valueChanges
      .pipe()
      .subscribe(newValue => {
        if (!processingChanges) {
          processingChanges = true;
          this.onFormGroupChanged(oldValue, newValue);
          oldValue = newValue;
          processingChanges = false;
        }
      });
    this.filterService
      .observeProjectFilter()
      .pipe(takeUntil(this.destroy$))
      .subscribe(filter => {
        this.updating = true;
        this.onFilterChanged(filter);
        this.updating = false;
      });
    this.projectListService
      .getUsedCountryCodes()
      .then(countryCodes => {
        this.allCountryCodes = countryCodes.sort(
          (a: string, b: string) => BY_ALPHA_2[a].name.localeCompare(BY_ALPHA_2[b].name)
        );
      });
    this.projectListService
      .getAllManagers()
      .then(allManagerMemberships => {
        this.onManagerMembershipsLoaded(allManagerMemberships);
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private onManagerMembershipsLoaded(allManagerMemberships: GetProjectManagers[]) {
    this.userIdToManager = {};
    const managers: FilterableManager[] = [];
    for (const manager of allManagerMemberships) {
      const filterableManager: FilterableManager = {
        userId: manager.user.id,
        name: manager.user.fullName,
        count: manager.projectIds.length,
        roles: manager.roles.map(role => role.id),
      };
      this.userIdToManager[filterableManager.userId] = filterableManager;
      managers.push(filterableManager);
    }
    this.allManagers = managers.sort((a, b) => a?.name?.localeCompare(b?.name));
    const selectedManagerId = this.formGroup.value.projectManager;
    if (selectedManagerId && !selectedManagerId.name) {
      this.formGroup.patchValue({projectManager: selectedManagerId});
    }
  }

  managerToId(manager: FilterableManager): string {
    return manager.userId;
  }

  managerOrUserIdToLabel(elem: any): string {
    if (elem && elem.name && elem.userId) {
      return this.managerToLabel(elem);
    } else {
      const manager = this.userIdToManager[elem];
      return manager ? this.managerToLabel(manager) : '';
    }
  }

  managerToLabel(manager: FilterableManager): string {
    return `${manager.name} in ${manager.count} Projects as ${manager.roles.join(' or ')}`;
  }

  private onFilterChanged(filter: ProjectFilter) {
    this.formGroup.patchValue({
      name: filter.projectName,
      projectManager: filter.managerUserId,
      myProjects: filter.myProjects,
      hybridProject: filter.hybridProject,
      managingApplicationMode: filter.managingApplicationMode,
      managingApplication: filter.managingApplication,
      status: filter.status,
      type: filter.type,
      phase: filter.phase,
      stage: filter.stage,
      businessModel: filter.businessModel,
      routeToMarket: filter.routeToMarket,
      developmentDirectorate: filter.developmentDirectorate,
      country: filter.countryCode.length === 0 ? [] : filter.countryCode,
      globalRegion: filter.globalRegion,
      devCluster: filter.developmentCluster,
      consCluster: filter.constructionCluster,
      region: filter.region,
      teams: filter.teamId,
      projectValidation: filter.projectValidation,
      validationLock: filter.validationLock,
    });
  }

  private onFormGroupChanged(_oldValue: any, newValue: any) {
    const countries: CountryAlpha2Code[] = newValue.country || this.allCountryCodes;
    const newRegions = this.createAllRegionsBySelectedCountries(countries);
    if (ProjectFilterSidebarComponent.haveRegionsChanged(this.allRegions, newRegions)) {
      this.allRegions = newRegions;
      this.cdr.detectChanges();
    }

    const currentlySelectedRegion: string[] = newValue.region;
    const currentlySelectedRegionIsValid = this.allRegions.some(group => currentlySelectedRegion.some(region => group.items.includes(region)));
    if (!currentlySelectedRegionIsValid) {
      this.formGroup.patchValue({
        region: [],
      });
    }
  }

  private createAllRegionsBySelectedCountries(countryCodes: CountryAlpha2Code[]): { label: string, items: any[] }[] {
    const countryCodesToUse = (countryCodes.length === 0 ? this.allCountryCodes : countryCodes) ?? [];
    return countryCodesToUse.map(countryCode => {
      const country = BY_ALPHA_2[countryCode];
      return {
        label: country.name,
        items: country.regions,
      };
    });
  }

  private static haveRegionsChanged(oldRegions: { label: string, items: any[] }[], newRegions: { label: string, items: any[] }[]): boolean {
    if ((oldRegions?.length ?? 0) !== (newRegions?.length ?? 0)) {
      return true;
    }
    for (let i = 0; i < (oldRegions?.length ?? 0); i++) {
      if (oldRegions[i].label !== newRegions[i].label) {
        return true;
      }
    }
    return false;
  }

  private onApplyFilter() {
    const fgValue = this.formGroup.value;
    this.filterService.patchFilter({
      projectName: fgValue.name,
      managerUserId: fgValue.projectManager?.userId ?? fgValue.projectManager,
      myProjects: fgValue.myProjects,
      hybridProject: fgValue.hybridProject,
      managingApplicationMode: fgValue.managingApplicationMode,
      managingApplication: fgValue.managingApplication,
      technology: this.generateFilteredTechnologies(),
      status: fgValue.status,
      type: fgValue.type,
      phase: fgValue.phase,
      stage: fgValue.stage,
      businessModel: fgValue.businessModel,
      routeToMarket: fgValue.routeToMarket,
      developmentDirectorate: fgValue.developmentDirectorate,
      countryCode: fgValue.country || [],
      globalRegion: fgValue.globalRegion,
      developmentCluster: fgValue.devCluster,
      constructionCluster: fgValue.consCluster,
      region: fgValue.region,
      teamId: fgValue.teams,
      projectValidation: fgValue.projectValidation,
      validationLock: fgValue.validationLock,
    });
  }

  private generateFilteredTechnologies(): Technology[] {
    const filteredTechs = this.allTechnologies.filter(tech => this.techFilter.technologyToIncludedInFilter[tech]);

    if(filteredTechs.length === this.allTechnologies.length) {
      return [];
    }

    return filteredTechs;
  }

  managingApplicationToLabel(value: ProjectManagingApplication): string {
    return projectManagingApplicationToLabel[value];
  }

  managingApplicationModeToLabel(value: ProjectManagingApplicationMode): string {
    return projectManagingApplicationModeToLabel[value];
  }

  hybridProjectFilterOptionToLabel(value: HybridProjectFilterOption): string {
    return hybridProjectFilterOptionToLabel[value];
  }

  myProjectsFilterOptionToLabel(value: MyProjectsFilterOption): string {
    return myProjectsFilterOptionToLabel[value];
  }

  directorateToLabel(value: DevelopmentDirectorate): string {
    return devDirectorateToLabel[value];
  }

  businessModeToLabel(value: BusinessModel): string {
    return businessModelToLabel[value];
  }

  routeToMarketToLabel(value: RouteToMarket): string {
    return routeToMarketToLabel[value];
  }

  technologyToLabel(value: Technology): string {
    return technologyToLabel[value];
  }

  typeToLabel(value: ProjectType): string {
    return projectTypeToLabel[value];
  }

  statusToLabel(value: ProjectStatus): string {
    return statusToLabel[value];
  }

  phaseToLabel(value: ProjectPhase): string {
    return phaseToLabel[value];
  }

  stageToLabel(value: ProjectStage): string {
    return stageToLabel[value];
  }

  countryCodeToLabel(value: CountryAlpha2Code): string {
    return BY_ALPHA_2[value].name;
  }

  globalRegionToLabel(value: GlobalRegion): string {
    return globalRegionToLabel[value];
  }

  devClusterToLabel(value: DevelopmentCluster): string {
    return devClusterToLabel[value];
  }

  consClusterToLabel(value: ConstructionCluster): string {
    return consClusterToLabel[value];
  }

  projectValidationToLabel(value: ProjectValidationFilter): string {
    return projectValidationFilterToLabel[value];
  }

  validationLockToLabel(value: ValidationLockFilter): string {
    return validationLockFilterToLabel[value];
  }

  regionToLabel(value: string): string {
    return value;
  }

  onResetClicked() {
    this.filterService.resetFilters();
    this.onApplyFilter();
  }

  onApplyClicked() {
    this.onApplyFilter();
  }

}
interface FilterableManager {
  userId: string;
  name: string;
  count: number;
  roles: string[];
}
